import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, matchPath } from 'react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { usePanelAyuda } from '../lib/ayuda.jsx'

/**
 * Paleta de comandos (⌘K / Ctrl+K). Roadmap §13.
 *
 * Saltar a cualquier proyecto o página escribiendo tres letras («uni env» → variables de
 * entorno de optimizacion-unei) y lanzar las acciones seguras (desplegar, nueva función).
 * Sin dependencias: un <dialog> nativo sobre los datos que ya están en la caché de TanStack.
 * La paleta NO añade permisos — solo enseña acciones que el rol del usuario ya permite.
 */

const PAGINAS = [
  { id: '', nombre: 'Panel de control', alias: 'panel inicio overview' },
  { id: 'deployments', nombre: 'Despliegues', alias: 'deploy builds' },
  { id: 'logs', nombre: 'Logs', alias: 'registros' },
  { id: 'sql', nombre: 'Editor SQL', alias: 'consulta query' },
  { id: 'tables', nombre: 'Explorador de tablas', alias: 'tablas esquema ddl' },
  { id: 'functions', nombre: 'Funciones', alias: 'scripts windmill' },
  { id: 'runs', nombre: 'Ejecuciones', alias: 'jobs' },
  { id: 'storage', nombre: 'Almacenamiento', alias: 's3 ficheros bucket cuota' },
  { id: 'metrics', nombre: 'Métricas', alias: 'metricas uso' },
  { id: 'env', nombre: 'Variables de entorno', alias: 'secretos configuracion' },
  { id: 'members', nombre: 'Miembros', alias: 'usuarios acceso' },
  { id: 'settings', nombre: 'Ajustes', alias: 'puerto repositorio configuracion' },
]

const norm = (t) => String(t ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

/** Todos los tokens escritos deben aparecer en el texto de la entrada, en cualquier orden. */
function casa(tokens, texto) {
  const t = norm(texto)
  return tokens.every((tk) => t.includes(tk))
}

export function Paleta({ platformAdmin }) {
  const nav = useNavigate()
  const { pathname } = useLocation()
  const ayuda = usePanelAyuda()
  const ref = useRef(null)
  const [abierta, setAbierta] = useState(false)
  const [texto, setTexto] = useState('')
  const [sel, setSel] = useState(0)

  const slugActual = (matchPath('/p/:slug/*', pathname) ?? matchPath('/p/:slug', pathname))?.params.slug ?? null

  const proyectos = useQuery({ queryKey: ['projects'], queryFn: api.projects, staleTime: 30_000, enabled: abierta })
  const desplegar = useMutation({
    mutationFn: (slug) => api.deploy(slug),
    onSuccess: (_, slug) => nav(`/p/${slug}/deployments`),
  })

  // El atajo vive en window: la paleta debe abrirse desde cualquier pantalla, también con el
  // foco dentro de un campo — es el convenio de ⌘K en todas partes.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setAbierta((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (abierta && !d.open) { setTexto(''); setSel(0); d.showModal() }
    if (!abierta && d.open) d.close()
  }, [abierta])

  const entradas = useMemo(() => {
    const lista = proyectos.data?.projects ?? []
    const tokens = norm(texto).split(/\s+/).filter(Boolean)
    const salida = []

    const anade = (e, peso) => salida.push({ ...e, peso })

    for (const p of lista) {
      if (p.state !== 'active' && p.state !== 'ready') continue
      const esActual = p.slug === slugActual
      // El proyecto en sí
      if (!tokens.length ? esActual === false : casa(tokens, `${p.slug} proyecto abrir`)) {
        anade({ id: `p:${p.slug}`, grupo: 'Proyectos', titulo: p.slug, detalle: p.domain ?? '',
                ejecutar: () => nav(`/p/${p.slug}`) }, (esActual ? 2 : 0) + (norm(p.slug).startsWith(tokens[0] ?? '') ? 3 : 0))
      }
      // Sus páginas: sin texto solo las del proyecto actual; con texto, las que casen
      for (const pag of PAGINAS) {
        // El id entra en el texto: «env» debe encontrar «Variables de entorno».
        const textoEntrada = `${p.slug} ${pag.id} ${pag.nombre} ${pag.alias}`
        if (tokens.length ? casa(tokens, textoEntrada) : esActual) {
          anade({ id: `p:${p.slug}:${pag.id}`, grupo: esActual ? 'Este proyecto' : 'Páginas',
                  titulo: pag.nombre, detalle: p.slug,
                  ejecutar: () => nav(`/p/${p.slug}${pag.id ? `/${pag.id}` : ''}`) },
                (esActual ? 5 : 0) + (casa(tokens, pag.nombre) ? 1 : 0))
        }
      }
      // Acciones, solo con el rol que ya las permite
      if (p.role === 'admin') {
        if (tokens.length ? casa(tokens, `${p.slug} desplegar deploy publicar`) : esActual) {
          anade({ id: `a:deploy:${p.slug}`, grupo: 'Acciones', titulo: `Desplegar ${p.slug}`,
                  detalle: 'lanza un build ahora',
                  ejecutar: () => desplegar.mutate(p.slug) }, esActual ? 4 : 0)
        }
        if (tokens.length && casa(tokens, `${p.slug} nueva funcion function crear`)) {
          anade({ id: `a:fn:${p.slug}`, grupo: 'Acciones', titulo: `Nueva función en ${p.slug}`,
                  detalle: 'abre el listado con el alta',
                  ejecutar: () => nav(`/p/${p.slug}/functions`) }, 0)
        }
      }
    }

    // Globales
    if (!tokens.length || casa(tokens, 'nuevo proyecto crear app')) {
      anade({ id: 'g:new', grupo: 'Consola', titulo: 'Nuevo proyecto', detalle: '/new',
              ejecutar: () => nav('/new') }, 1)
    }
    if (!tokens.length || casa(tokens, 'conectar claude mcp chatgpt cursor asistente')) {
      anade({ id: 'g:mcp', grupo: 'Consola', titulo: 'Conectar Claude (MCP)', detalle: 'ayuda',
              ejecutar: () => ayuda?.abrirTema('conectar') }, 0)
    }
    if (!tokens.length || casa(tokens, 'ayuda contrato documentacion')) {
      anade({ id: 'g:ayuda', grupo: 'Consola', titulo: ayuda?.abierta ? 'Cerrar la ayuda' : 'Abrir la ayuda',
              detalle: 'el panel «?»', ejecutar: () => ayuda?.alternar() }, 0)
    }
    if (platformAdmin) {
      for (const [ruta, nombre, alias] of [
        ['/admin/health', 'Diagnóstico de la plataforma', 'salud health minio dokploy'],
        ['/admin/sessions', 'Sesiones y tokens MCP', 'revocar'],
        ['/admin/audit', 'Registro de auditoría', 'audit log'],
      ]) {
        if (!tokens.length || casa(tokens, `${nombre} ${alias} admin`)) {
          anade({ id: `g:${ruta}`, grupo: 'Administración', titulo: nombre, detalle: ruta,
                  ejecutar: () => nav(ruta) }, 0)
        }
      }
    }

    salida.sort((a, b) => b.peso - a.peso)
    const corte = salida.slice(0, 14)
    const ORDEN = ['Este proyecto', 'Acciones', 'Proyectos', 'Páginas', 'Consola', 'Administración']
    corte.sort((a, b) => (ORDEN.indexOf(a.grupo) - ORDEN.indexOf(b.grupo)) || (b.peso - a.peso))
    return corte
  }, [proyectos.data, texto, slugActual, platformAdmin, ayuda, nav, desplegar])

  const lanzar = (e) => {
    if (!e) return
    setAbierta(false)
    e.ejecutar()
  }

  const alTeclear = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, entradas.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); lanzar(entradas[sel]) }
  }

  // Agrupar para pintar, conservando el orden por peso dentro del límite ya cortado.
  let grupoPrevio = null

  return (
    <dialog ref={ref} className="paleta" onCancel={(e) => { e.preventDefault(); setAbierta(false) }}
            onClick={(e) => { if (e.target === ref.current) setAbierta(false) }}>
      <input
        className="paleta-input" placeholder="Ir a… (proyecto, página o acción)"
        value={texto} autoFocus spellCheck="false"
        onChange={(e) => { setTexto(e.target.value); setSel(0) }}
        onKeyDown={alTeclear}
        role="combobox" aria-expanded="true" aria-controls="paleta-lista"
      />
      <div className="paleta-lista" id="paleta-lista" role="listbox">
        {entradas.length === 0 && <div className="paleta-vacia">Nada que case con «{texto}»</div>}
        {entradas.map((e, i) => {
          const cabecera = e.grupo !== grupoPrevio ? e.grupo : null
          grupoPrevio = e.grupo
          return (
            <div key={e.id}>
              {cabecera && <div className="paleta-grupo">{cabecera}</div>}
              <button
                type="button" role="option" aria-selected={i === sel}
                className={`paleta-item ${i === sel ? 'activa' : ''}`}
                onMouseEnter={() => setSel(i)} onClick={() => lanzar(e)}
              >
                <span>{e.titulo}</span>
                <span className="paleta-detalle">{e.detalle}</span>
              </button>
            </div>
          )
        })}
      </div>
      <div className="paleta-pie">↑↓ moverse · ↵ ir · esc cerrar</div>
    </dialog>
  )
}
